import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'components/core/Button';
import { Text } from 'components/core/Typography';
import ChevronDown from 'assets/images/chevron-arrows.svg';
import { Image } from "@sparefoot/react-commons";
import IconDeals from 'assets/images/icon-deals.svg';
import checkWhite from 'assets/images/check-white.svg';

import { DEALS_FILTER_OPTIONS } from 'config/dealFilter';

import './DealsFilter.scss';

export default function DealsFilter({ value = 'all', onChange }) {
	const [isOpen, setIsOpen] = useState(false);
	const containerRef = useRef(null);
	const selectedOption =
		DEALS_FILTER_OPTIONS.find((opt) => opt.value === value) ||
		DEALS_FILTER_OPTIONS[0];

	const handleClick = useCallback(() => {
		setIsOpen((prev) => !prev);
	}, []);

	const handleChange = useCallback(
		(selValue) => {
			setIsOpen(false);
			if (onChange) {
				onChange(selValue);
			}
		},
		[onChange],
	);

	useEffect(() => {
		if (!isOpen) return;

		function handleClickOutside(event) {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target)
			) {
				setIsOpen(false);
			}
		}

		function handleCloseOnScrollOrResize() {
			setIsOpen(false);
		}

		document.addEventListener('mousedown', handleClickOutside);
		window.addEventListener('scroll', handleCloseOnScrollOrResize, true);
		window.addEventListener('resize', handleCloseOnScrollOrResize);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			window.removeEventListener(
				'scroll',
				handleCloseOnScrollOrResize,
				true,
			);
			window.removeEventListener('resize', handleCloseOnScrollOrResize);
		};
	}, [isOpen]);

	return (
		<div
			className="deals-filter-container"
			ref={containerRef}
		>
			<Button
				className="deals-filter-selected"
				onClick={handleClick}
				aria-expanded={isOpen}
				aria-haspopup="listbox"
				aria-label="Filter deals options"
				tabIndex={0}
			>
				<Text
					color="white"
					weight="bold"
				>
					<img
						src={IconDeals}
						alt=""
					/>
					{selectedOption.label}
				</Text>
				<Image
					src={ChevronDown}
				/>
			</Button>
			{isOpen && (
				<ul
					className="deals-filter-options"
					role="listbox"
					aria-label="Filter deals options list"
				>
					<hr />
					{DEALS_FILTER_OPTIONS.map((option) => (
						<li
							className="option"
							key={option.value}
							role="option"
							aria-selected={
								selectedOption.value === option.value
							}
						>
							<Button
								className={
									selectedOption.value === option.value
										? 'option-button active'
										: 'option-button'
								}
								onClick={() => handleChange(option.value)}
							>
								{option.label}
								{selectedOption.value === option.value && (
									<Image
										src={checkWhite}
									/>
								)}
							</Button>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

DealsFilter.propTypes = {
	value: PropTypes.oneOf(DEALS_FILTER_OPTIONS.map((o) => o.value)),
	onChange: PropTypes.func,
};
